import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  Loader2, ChevronDown, ChevronRight, RefreshCw, Link as LinkIcon,
  LogIn, CheckCircle2, XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Row-level actions per list key. Each action declares its label, icon,
 * a `visible(row)` guard, and an `onRun(row)` async handler that calls
 * the relevant existing endpoint. After success the list reloads.
 */
const ROW_ACTIONS = {
  // CSM tickets — close (not already terminal)
  tickets: [{
    key: 'close', label: 'Close', Icon: CheckCircle2,
    className: 'text-emerald-600 hover:bg-emerald-50',
    visible: (row) => !['closed', 'resolved', 'cancelled'].includes((row.status || '').toLowerCase().trim()),
    confirm: (row) => `Close ticket ${row.ticket_number}?`,
    run: (row) => axios.patch(
      `${API_URL}/api/csm/tickets/${row.id}`,
      { status: 'closed' },
      { headers: authHeader() },
    ),
  }],
  related_tickets: 'tickets',
  owner_tickets:   'tickets',

  // Assets — quick check-in (when assigned)
  assets: [{
    key: 'checkin', label: 'Check in', Icon: LogIn,
    className: 'text-sky-600 hover:bg-sky-50',
    visible: (row) => (row.status || '').toLowerCase().trim() === 'assigned',
    confirm: (row) => `Check in asset ${row.asset_tag}?`,
    run: (row) => axios.post(
      `${API_URL}/api/assets/${row.id}/checkin`,
      {},
      { headers: authHeader() },
    ),
  }],
  requester_assets: 'assets',

  // Access requests — approve / reject (org access flow)
  access_requests: [
    {
      key: 'approve', label: 'Approve', Icon: CheckCircle2,
      className: 'text-emerald-600 hover:bg-emerald-50',
      visible: (row) => (row.status || '').toLowerCase().trim() === 'pending',
      confirm: (row) => `Approve access request from ${row.organization}?`,
      run: (row) => axios.post(
        `${API_URL}/api/access-requests/${row.id}/approve`,
        {},
        { headers: authHeader() },
      ),
    },
    {
      key: 'reject', label: 'Reject', Icon: XCircle,
      className: 'text-rose-600 hover:bg-rose-50',
      visible: (row) => (row.status || '').toLowerCase().trim() === 'pending',
      confirm: (row) => `Reject access request from ${row.organization}?`,
      run: (row) => axios.post(
        `${API_URL}/api/access-requests/${row.id}/reject`,
        {},
        { headers: authHeader() },
      ),
    },
  ],
};

function resolveActions(listKey) {
  const v = ROW_ACTIONS[listKey];
  if (typeof v === 'string') return ROW_ACTIONS[v] || [];
  return v || [];
}

/**
 * RelatedLists — collapsible tabular widgets surfacing joined records
 * (tickets, assets, voice notes, form submissions...) below a parent
 * detail screen.
 *
 * Props:
 *  - scope:       'user' | 'ticket' | 'asset'
 *  - id:          record id (or 'me' for self user)
 *  - defaultOpen?: string[]   (list keys that start expanded)
 *  - showActions?: boolean    (render row-level action buttons; default true)
 */
export default function RelatedLists({ scope, id, defaultOpen, showActions = true }) {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openKeys, setOpenKeys] = useState(() => new Set(defaultOpen || []));
  const [busyRow, setBusyRow] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API_URL}/api/related/${scope}/${id}`, { headers: authHeader() });
      const newLists = r.data.lists || [];
      setLists(newLists);
      if (!defaultOpen && newLists.length && openKeys.size === 0) {
        const firstWithRows = newLists.find(l => (l.rows || []).length > 0) || newLists[0];
        if (firstWithRows) setOpenKeys(new Set([firstWithRows.key]));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load related items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, id]);

  const toggle = (key) => {
    setOpenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const runAction = async (e, listKey, row, action) => {
    e.stopPropagation();
    const msg = action.confirm ? action.confirm(row) : `${action.label}?`;
    if (msg && !window.confirm(msg)) return;
    setBusyRow(`${listKey}:${row.id}:${action.key}`);
    try {
      await action.run(row);
      toast.success(`${action.label} done`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `${action.label} failed`);
    } finally {
      setBusyRow(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 border-[#E2E8F0]" data-testid="related-lists-loading">
        <Loader2 className="w-5 h-5 text-[#0E7490] animate-spin inline mr-2" />
        <span className="text-sm text-[#64748B]">Loading related items…</span>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="p-4 border-rose-200 bg-rose-50" data-testid="related-lists-error">
        <p className="text-sm text-rose-700">{error}</p>
      </Card>
    );
  }
  if (!lists.length) return null;

  return (
    <div className="space-y-3" data-testid="related-lists">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LinkIcon className="w-4 h-4 text-[#0E7490]" />
          <h3 className="text-sm font-semibold text-[#0A2342] uppercase tracking-wide">Related Lists</h3>
        </div>
        <Button size="sm" variant="ghost" onClick={load} data-testid="related-lists-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {lists.map((list) => {
        const isOpen = openKeys.has(list.key);
        const actions = showActions ? resolveActions(list.key) : [];
        return (
          <Card key={list.key} className="border-[#E2E8F0]" data-testid={`related-list-${list.key}`}>
            <button
              type="button"
              onClick={() => toggle(list.key)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#F8FAFC] transition-colors"
              data-testid={`related-list-toggle-${list.key}`}
            >
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="w-4 h-4 text-[#0E7490]" /> : <ChevronRight className="w-4 h-4 text-[#64748B]" />}
                <span className="text-sm font-semibold text-[#0A2342]">{list.label}</span>
                <span className="text-xs font-medium text-[#0E7490] bg-[#0E7490]/10 px-2 py-0.5 rounded-full">
                  {list.count}
                </span>
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-[#E2E8F0]">
                {(list.rows || []).length === 0 ? (
                  <div className="px-4 py-6 text-center text-xs text-[#94A3B8]" data-testid={`related-list-empty-${list.key}`}>
                    No records.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[#64748B] border-b border-[#E2E8F0] bg-[#F8FAFC]">
                          {list.columns.map(c => (
                            <th key={c.key} className="px-3 py-2 font-medium whitespace-nowrap">{c.label}</th>
                          ))}
                          {actions.length > 0 && <th className="px-3 py-2 font-medium text-right">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {list.rows.map((row) => {
                          const href = list.link ? list.link.replace('{id}', row.id) : null;
                          return (
                            <tr
                              key={row.id || Math.random()}
                              onClick={href ? () => navigate(href) : undefined}
                              className={`border-b border-[#F1F5F9] ${href ? 'hover:bg-[#0E7490]/5 cursor-pointer' : 'hover:bg-[#F8FAFC]'}`}
                              data-testid={`related-row-${list.key}-${row.id || ''}`}
                            >
                              {list.columns.map((c) => (
                                <td key={c.key} className="px-3 py-2 text-[#0A2342] whitespace-nowrap align-top">
                                  {formatCell(row[c.key], c.key)}
                                </td>
                              ))}
                              {actions.length > 0 && (
                                <td className="px-2 py-1 text-right whitespace-nowrap align-top">
                                  {actions
                                    .filter(a => a.visible(row))
                                    .map(a => {
                                      const Icon = a.Icon;
                                      const busy = busyRow === `${list.key}:${row.id}:${a.key}`;
                                      return (
                                        <button
                                          key={a.key}
                                          type="button"
                                          disabled={busy}
                                          onClick={(e) => runAction(e, list.key, row, a)}
                                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${a.className} disabled:opacity-50`}
                                          data-testid={`row-action-${list.key}-${a.key}-${row.id}`}
                                          title={a.label}
                                        >
                                          {busy
                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                            : <Icon className="w-3 h-3" />}
                                          <span className="hidden sm:inline">{a.label}</span>
                                        </button>
                                      );
                                    })}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function formatCell(value, key) {
  if (value === null || value === undefined || value === '') return <span className="text-[#94A3B8]">—</span>;
  if (typeof value === 'string' && /_at$|next_service/.test(key) && /\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      const d = new Date(value);
      if (!isNaN(d)) return d.toLocaleString();
    } catch { /* fall through */ }
  }
  if (key === 'asset_tag' || key === 'ticket_number') {
    return <span className="font-mono">{value}</span>;
  }
  if (key === 'status') {
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#0E7490]/10 text-[#0E7490] capitalize">{value}</span>;
  }
  if (key === 'priority' && typeof value === 'string' && /^P[1-4]$/.test(value)) {
    const map = { P1: 'bg-rose-100 text-rose-700', P2: 'bg-orange-100 text-orange-700', P3: 'bg-amber-100 text-amber-700', P4: 'bg-slate-100 text-slate-600' };
    return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${map[value] || ''}`}>{value}</span>;
  }
  return String(value);
}
